import { Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

export enum ScreenSize {
  Mobile = 'mobile',    // < 768px
  Tablet = 'tablet',    // 768px - 1199px
  Desktop = 'desktop',  // 1200px - 1599px
  Large = 'large'       // >= 1600px
}

export interface LayoutConfig {
  inspectorMode: 'modal' | 'bottom-sheet' | 'floating' | 'docked';
  paletteMode: 'drawer' | 'collapsible' | 'sidebar';
  maxVisibleProperties: number;
  showStickyActions: boolean;
  compactMode: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ResponsiveLayoutService {
  constructor(private breakpointObserver: BreakpointObserver) {}

  screenSize$: Observable<ScreenSize> = this.breakpointObserver.observe([
    '(max-width: 767px)',
    '(min-width: 768px) and (max-width: 1199px)',
    '(min-width: 1200px) and (max-width: 1599px)',
    '(min-width: 1600px)'
  ]).pipe(
    map(result => this.determineScreenSize(result)),
    distinctUntilChanged()
  );

  layoutConfig$: Observable<LayoutConfig> = this.screenSize$.pipe(
    map(screenSize => this.getLayoutConfig(screenSize))
  );

  private determineScreenSize(breakpointState: any): ScreenSize {
    if (breakpointState.breakpoints['(max-width: 767px)']) {
      return ScreenSize.Mobile;
    } else if (breakpointState.breakpoints['(min-width: 768px) and (max-width: 1199px)']) {
      return ScreenSize.Tablet;
    } else if (breakpointState.breakpoints['(min-width: 1200px) and (max-width: 1599px)']) {
      return ScreenSize.Desktop;
    } else {
      return ScreenSize.Large;
    }
  }

  getLayoutConfig(screenSize: ScreenSize): LayoutConfig {
    switch (screenSize) {
      case ScreenSize.Mobile:
        return {
          inspectorMode: 'modal',
          paletteMode: 'drawer',
          maxVisibleProperties: 5,
          showStickyActions: true,
          compactMode: true,
        };
      case ScreenSize.Tablet:
        return {
          inspectorMode: 'bottom-sheet',
          paletteMode: 'collapsible',
          maxVisibleProperties: 8,
          showStickyActions: true,
          compactMode: true,
        };
      case ScreenSize.Desktop:
        return {
          inspectorMode: 'floating',
          paletteMode: 'sidebar',
          maxVisibleProperties: 12,
          showStickyActions: true,
          compactMode: false,
        };
      case ScreenSize.Large:
        return {
          inspectorMode: 'floating',
          paletteMode: 'sidebar',
          maxVisibleProperties: 15,
          showStickyActions: true,
          compactMode: false,
        };
      default:
        return {
          inspectorMode: 'floating',
          paletteMode: 'sidebar',
          maxVisibleProperties: 12,
          showStickyActions: true,
          compactMode: false,
        };
    }
  }

  isCompactMode(screenSize: ScreenSize): boolean {
    return screenSize === ScreenSize.Mobile || screenSize === ScreenSize.Tablet;
  }

  isMobile(): Observable<boolean> {
    return this.screenSize$.pipe(
      map(size => size === ScreenSize.Mobile)
    );
  }

  isTablet(): Observable<boolean> {
    return this.screenSize$.pipe(
      map(size => size === ScreenSize.Tablet)
    );
  }

  isDesktop(): Observable<boolean> {
    return this.screenSize$.pipe(
      map(size => size === ScreenSize.Desktop || size === ScreenSize.Large)
    );
  }
}
